import { _isEmptyString, _isNil, _isNotBlank, _notNil } from '@/littledash';
import { fromInVivoValidator, type HybridValidator, type InVivoValidatorResult } from '@/support/hotValidator.util';
import type { CellValue } from '@/support/hot.types';
import { BufferedValidationRequestCache, treatmentTypeNameValidationCache } from '@/utils/ValidationRequestCache';
import type { components } from '@/generated/internal-api/openapi-types';
import { isApiId } from '@/utils/ApiId.utils';

type TreatmentTypeGlossary = components['schemas']['TreatmentTypeGlossaryV1'];
type TreatmentTypeApiId = components['schemas']['TreatmentTypeApiId'];

export type TreatmentTypeRow = Pick<TreatmentTypeGlossary, 'name'>;
export type TreatmentTypeEditRow = Pick<TreatmentTypeGlossary, 'name' | 'api_id'>;
type TreatmentGridRow = TreatmentTypeRow | TreatmentTypeEditRow;

/** Property name of the treatment name column, shared by both grids. */
export const NAME_PROPERTY = 'name';

export const MAX_TREATMENT_NAME_LENGTH = 255;

export const TREATMENT_NAME_ERRORS = {
  required: 'Treatment name is required',
  tooLong: `Treatment name must be ${MAX_TREATMENT_NAME_LENGTH} characters or less`,
  duplicateInGrid: 'Treatment name must be unique within the spreadsheet',
  alreadyExists: 'Treatment name already exists in this team',
  couldNotVerify: 'Could not verify',
  notValid: 'Treatment name is not valid',
} as const;

interface BuildTreatmentNameValidatorParams {
  treatments?: Array<TreatmentTypeGlossary>;
  validateName?: (name: string) => Promise<InVivoValidatorResult>;
}

/**
 * The rows currently held by the grid, as the row objects the grid was seeded with.
 *
 * `getSourceData` rather than `getData`: Handsontable's `getData` always renders rows as arrays
 * of cell values regardless of how the dataset was supplied, whereas the source data keeps the
 * `name`/`api_id` shape these rules are written against.
 */
const getSpreadsheetData = (instance: unknown): Array<TreatmentGridRow> => {
  if (_notNil(instance) && typeof (instance as { getSourceData?: unknown }).getSourceData === 'function') {
    return (instance as { getSourceData: () => Array<TreatmentGridRow> }).getSourceData() ?? [];
  }
  return [];
};

/** The `api_id` a row carries, or undefined for a create-mode row that has none. */
const getRowApiId = (row: TreatmentGridRow | undefined): TreatmentTypeApiId | undefined =>
  isApiId('tty', (row as TreatmentTypeEditRow)?.api_id) ? (row as TreatmentTypeEditRow).api_id : undefined;

/**
 * Normalised names of the treatments being edited, excluding the row identified by
 * `currentApiId`. A name matching one of these is still held on the server by another row, so
 * the server would reject it — reporting that locally keeps the reason accurate and offline.
 */
const buildOtherOriginalNames = (
  treatments: Array<TreatmentTypeGlossary>,
  currentApiId: string | undefined
): Set<string> =>
  treatments.reduce<Set<string>>((names, { api_id, name }) => {
    if (api_id !== currentApiId && _isNotBlank(name)) {
      names.add(BufferedValidationRequestCache.normalise(name));
    }
    return names;
  }, new Set());

/**
 * Synchronous rules for a treatment name, evaluated before any network request.
 * Returns the failure reason, or undefined when the name passes every local rule.
 */
const getSyncErrorMessage = (
  value: unknown,
  rowIndex: number,
  gridData: Array<TreatmentGridRow>,
  otherOriginalNames: Set<string> = new Set()
): string | undefined => {
  if (_isNil(value) || typeof value !== 'string') {
    return TREATMENT_NAME_ERRORS.required;
  }

  const trimmed = value.trim();
  if (_isEmptyString(trimmed)) {
    return TREATMENT_NAME_ERRORS.required;
  }

  if (trimmed.length > MAX_TREATMENT_NAME_LENGTH) {
    return TREATMENT_NAME_ERRORS.tooLong;
  }

  const normalised = BufferedValidationRequestCache.normalise(trimmed);
  const hasDuplicate = gridData.some(({ name: otherName }, otherRow) => {
    if (otherRow === rowIndex || _isNil(otherName) || typeof otherName !== 'string') {
      return false;
    }
    const otherTrimmed = otherName.trim();
    if (_isEmptyString(otherTrimmed)) {
      return false;
    }
    return BufferedValidationRequestCache.normalise(otherTrimmed) === normalised;
  });

  if (hasDuplicate || otherOriginalNames.has(normalised)) {
    return TREATMENT_NAME_ERRORS.duplicateInGrid;
  }

  return undefined;
};

/**
 * Resolves the treatment a grid row belongs to by the `api_id` carried in its hidden column,
 * so a row's identity survives any reordering of the grid.
 */
export const findTreatmentForRow = (
  treatments: Array<TreatmentTypeGlossary>,
  rowApiId: unknown
): TreatmentTypeGlossary | undefined =>
  typeof rowApiId === 'string' && _isNotBlank(rowApiId)
    ? treatments.find(({ api_id }) => api_id === rowApiId)
    : undefined;

/**
 * Builds the validator for the treatment name column, as a hybrid validator: a Handsontable
 * validator carrying its underlying {@link InVivoValidator} so a caller can re-run it to recover
 * the *reason* a cell failed, which Handsontable's own `afterValidate` hook does not report.
 *
 * Local rules (required, length, in-grid uniqueness) run first so blank and obviously invalid
 * cells never reach the network. Only a name that passes them all is sent to the remote
 * uniqueness check, which batches concurrent cells into one request via
 * {@link treatmentTypeNameValidationCache}.
 *
 * When `treatments` is supplied (edit mode) a name left unchanged is accepted without a
 * request, since the server would otherwise report it as taken by the very row being edited.
 */
export const buildTreatmentNameValidator = ({
  treatments,
  validateName = (name) => treatmentTypeNameValidationCache.validate(name),
}: BuildTreatmentNameValidatorParams = {}): HybridValidator =>
  fromInVivoValidator(async (value: CellValue, cellProperties): Promise<InVivoValidatorResult> => {
    const { row: rowIndex, instance } = cellProperties as { row: number; instance?: unknown };
    const gridData = getSpreadsheetData(instance);
    const existingTreatment = _notNil(treatments)
      ? findTreatmentForRow(treatments, getRowApiId(gridData[rowIndex]))
      : undefined;

    const syncError = getSyncErrorMessage(
      value,
      rowIndex,
      gridData,
      _notNil(treatments) ? buildOtherOriginalNames(treatments, existingTreatment?.api_id) : undefined
    );
    if (_notNil(syncError)) {
      return { valid: false, message: syncError };
    }

    const trimmed = (value as string).trim();

    const existingName = existingTreatment?.name;
    if (
      _notNil(existingName) &&
      BufferedValidationRequestCache.normalise(existingName) === BufferedValidationRequestCache.normalise(trimmed)
    ) {
      return { valid: true };
    }

    try {
      return await validateName(trimmed);
    } catch {
      return { valid: false, message: TREATMENT_NAME_ERRORS.couldNotVerify };
    }
  });
